import React, { Component, Fragment } from 'react'

import { any, contains, path, pluck, startsWith } from 'ramda'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { FeatureBadge } from '@driftt/dds'
import { Div } from '@driftt/tide-core'
import { Subnav } from '@driftt/tide-subnav'
import { access as ACCESS } from '@driftt/traffic-control'

import { fetchUserProperties } from 'api/userAPI'
import {
  BRANDED_WIDGET_PAGE_TITLES,
  HEADER_NAVS,
  STANDARD_WIDGET_PAGE_TITLES,
  V2_SETTINGS_BASE_ROUTES,
  V2_SETTINGS_EXACT_ROUTES,
} from 'components/constants/settings'
import config from 'config'
import {
  receiveHasMultiEmbed,
  receiveHasMultiLanguageDowngrade,
} from 'modules/multiEmbed/actions'
import { recieveUserProperties } from 'modules/user/actions'
import * as UserSelectors from 'modules/user/selectors'

import { getWidgetSettingsUrl } from './const'
import SettingsContent from './SettingsContent'
import SettingsPageHeader from './SettingsPageHeader/index.js'

import './style.css'

class Settings extends Component {
  constructor(props) {
    super(props)

    this.state = {
      isAdmin: ACCESS.isAdmin(),
      isPremium: ACCESS.hasGates('premium_drift'),
      pageTitle: this.getSettingsPageTitle(),
      pageBreadcrumbs: this.getSettingsBreadcrumbs(),
      showBadge: false,
    }
  }

  componentDidMount() {
    this.getUserProperties()
    this.props.actions.receiveHasMultiEmbed(ACCESS.hasGates('multi_language'))
    this.props.actions.receiveHasMultiLanguageDowngrade(
      !ACCESS.hasGates('multi_language') &&
        ACCESS.hasGates('is_multi_language_downgrade')
    )

    const START_DATE = new Date('2026-01-13T00:00:00Z')
    const END_DATE = new Date('2026-02-15T23:59:59Z')
    const currentDate = new Date()
    const releaseMessaging = localStorage.getItem('release-messaging')

    if (releaseMessaging) {
      this.setState({ showBadge: !JSON.parse(releaseMessaging)?.isExpired })
      if (currentDate < START_DATE || currentDate > END_DATE) {
        localStorage.removeItem('release-messaging')
        this.setState({ showBadge: false })
      }
    } else if (currentDate >= START_DATE && currentDate <= END_DATE) {
      localStorage.setItem(
        'release-messaging',
        JSON.stringify({ hasSeenPopup: false, isExpired: false })
      )
      this.setState({ showBadge: true })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({
        pageTitle: this.getSettingsPageTitle(),
        pageBreadcrumbs: this.getSettingsBreadcrumbs(),
      })
    }
  }

  getSettingsPageTitle = () => {
    const {
      location: { pathname },
    } = this.props
    const pageTitles = ACCESS.hasGates('brand_management')
      ? BRANDED_WIDGET_PAGE_TITLES
      : STANDARD_WIDGET_PAGE_TITLES

    const pathObj = pageTitles.find(function (element) {
      return element.path === pathname
    })
    return pathObj ? pathObj.title : ''
  }

  getSettingsBreadcrumbs = () => {
    const {
      location: { pathname },
    } = this.props
    const pageTitles = ACCESS.hasGates('brand_management')
      ? BRANDED_WIDGET_PAGE_TITLES
      : STANDARD_WIDGET_PAGE_TITLES

    const pathObj = pageTitles.find(function (element) {
      return element.path === pathname
    })
    return pathObj ? pathObj.breadcrumbs : []
  }

  getUserProperties = async () => {
    const {
      userId,
      actions: { recieveUserProperties },
    } = this.props
    await fetchUserProperties(userId)
      .then(recieveUserProperties)
      .catch((error) => {
        console.error(error)
      })
  }

  getIsV2SettingsPage = ({ pathname }) => {
    const paths = pluck('path', V2_SETTINGS_EXACT_ROUTES)
    const matchedBasePath = any(
      (baseRoute) => startsWith(baseRoute.path, pathname),
      V2_SETTINGS_BASE_ROUTES
    )
    const hasExactPath = contains(pathname, paths)

    return matchedBasePath || hasExactPath
  }

  createSubnav = () => {
    const {
      hasAccount,
      hasAdminSettings,
      hasProfile,
      hasEmail,
      hasMeetings,
      hasBilling,
      hasManageTeammates,
      hasTeams,
      hasOrgMeetings = false,
      hasDataPrivacy,
      hasSecurity,
      hasWidget,
      hasConvoRouting,
      hasBot,
      hasConversations,
      hasNotifications,
      hasCompany,
      hasMeetingsInSubnav,
      hasIntegrations,
      hasHelpDocs,
    } = this.props.appConfig

    const { isPremium, showBadge } = this.state

    const mySettingsNavigationItems = [
      {
        label: 'MY SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Account',
            path: '/account',
            hide: !hasAccount,
          },
          {
            label: 'Notifications',
            path: '/notifications',
            hide: !hasNotifications,
          },
          {
            label: 'Meetings',
            path: '/meetings',
            hide: !hasMeetingsInSubnav,
          },
          {
            label: 'Drift Profile',
            path: '/profile',
            hide: !hasProfile,
          },
          {
            label: 'Email',
            path: '/email',
            hide: !hasEmail,
          },
        ],
      },
    ]

    const hasCrmGate = ACCESS.hasGates('prospector_crm_settings')
    const hasProspector = hasCrmGate
      ? ACCESS.isAdmin() ||
        ACCESS.hasFeatures([
          {
            featureName: 'filter_admin_settings',
            accessLevel: 'WRITE',
          },
        ])
      : false

    const hasWidgetV3 = ACCESS.hasGates('has_widget_v3')
    const hasEnhancedWidgetSettings = ACCESS.hasGates(
      'has_enhanced_widget_settings'
    )

    const orgSettingsNavigationItems = [
      {
        label: 'ORGANIZATION SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'AI features',
            path: '/gpt',
            hide: !ACCESS.isAdmin(),
            exact: false,
          },
          {
            label: 'Plan and billing',
            path: '/billing',
            hide: !hasBilling,
          },
          {
            label: 'Manage teammates',
            path: '/team',
            hide: !hasManageTeammates,
            exact: false,
          },
          {
            label: 'Teams',
            path: '/teams',
            hide: !hasTeams || ACCESS.hasGates('workspace_settings'),
          },
          {
            label: 'Company details',
            path: '/company',
            hide: !hasCompany,
          },
          {
            label: 'Data privacy',
            path: hasWidgetV3 ? '/data-privacy' : '/consent',
            hide: !hasDataPrivacy,
          },
          {
            label: 'Security',
            path: '/security',
            exact: false,
            hide: !hasSecurity,
          },
          {
            label: 'Workspaces and teams',
            path: '/workspaces',
            exact: false,
            hide: !ACCESS.hasFeatures([
              { featureName: 'workspace_settings', accessLevel: 'READ' },
            ]),
          },
        ],
      },
    ]

    const appSettingsNavigationItems = [
      {
        label: 'APP SETTINGS',
        expanded: true,
        nodes: [
          {
            label:
              hasWidgetV3 || hasEnhancedWidgetSettings ? (
                <Fragment>
                  Chatbot &nbsp;
                  {hasEnhancedWidgetSettings && showBadge ? (
                    <FeatureBadge variant="new" size="small" />
                  ) : null}
                </Fragment>
              ) : (
                'Drift Widget'
              ),
            path: `/${getWidgetSettingsUrl()}`,
            exact: true,
            hide: !hasWidget,
          },
          {
            label: 'Conversations',
            path: '/conversations',
            hide: !hasConversations || hasEnhancedWidgetSettings,
          },
          {
            label: 'Conversation routing',
            path: '/rules',
            hide: !hasConvoRouting,
          },
          {
            label: 'Bot',
            path: '/bot',
            icon: !isPremium ? 'lock' : null,
            disabled: !isPremium,
            hide: !hasBot || hasEnhancedWidgetSettings,
          },
          {
            label: 'Meetings',
            path: '/meetings',
            hide: !hasMeetings && !hasOrgMeetings,
            exact: false,
          },
          {
            label: 'Prospector',
            path: '/prospector',
            hide: !hasProspector,
          },
        ],
      },
    ]

    const adminNavigationItems = [
      ...orgSettingsNavigationItems,
      ...appSettingsNavigationItems,
      {
        label: 'Integrations',
        path: '/integrations',
        exact: false,
        hide: !hasIntegrations,
      },
      {
        label: 'Help',
        path: '/help',
      },
    ]

    const subnavItems = [
      ...mySettingsNavigationItems,
      ...(hasAdminSettings
        ? adminNavigationItems
        : [
            {
              label: 'Help Docs',
              path: config.HELP_CENTER_HELPDOC_URL,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
            {
              label: 'Integrations',
              path: '/integrations',
              exact: false,
              hide: !hasIntegrations,
            },
          ]),
    ]

    const hasMySettings = mySettingsNavigationItems[0].nodes.some(
      (node) => !node.hide
    )
    const hasOrgSettings = orgSettingsNavigationItems[0].nodes.some(
      (node) => !node.hide
    )
    const hasAppSettings = appSettingsNavigationItems[0].nodes.some(
      (node) => !node.hide
    )

    const rbacUserSubnavItems = [
      ...(hasMySettings ? mySettingsNavigationItems : []),
      ...(hasOrgSettings ? orgSettingsNavigationItems : []),
      ...(hasAppSettings ? appSettingsNavigationItems : []),
      {
        label: 'Integrations',
        path: '/integrations',
        exact: false,
        hide: !hasIntegrations,
      },
      {
        label: 'Help Docs',
        path: config.HELP_CENTER_HELPDOC_URL,
        target: '_blank',
        rel: 'noopener noreferrer',
        hide: !hasHelpDocs,
      },
    ]

    return (
      <Subnav
        title="Settings"
        open={true}
        nodes={ACCESS.isRbacUser() ? rbacUserSubnavItems : subnavItems}
      />
    )
  }

  render() {
    const { orgId, location, hasDesktopApp } = this.props
    const { pageTitle, pageBreadcrumbs } = this.state
    const hasBrands = ACCESS.hasGates('brand_management')

    const isV2Settings = this.getIsV2SettingsPage(location)

    const isDealsPage = location.pathname.includes('deal-admin')
    const isBrands = location.pathname.includes('widget') && hasBrands
    const isViewBrand =
      location.pathname.includes('widget/brand/view') && hasBrands

    const cardLayoutPages = Object.freeze({
      'Manage teammates': 'manage-teammates-content-container',
      Teams: 'settings-card-content-container',
      Account: 'account-content-container',
      'Data privacy': 'data-protection-page-content',
      'Drift Widget': 'settings-card-content-container',
      Email: 'email-content-container',
      'Teammate activity log': 'settings-teammate-logs',
      Workspaces: 'workspaces-management-content-container',
      Notifications: 'notifications-content-container',
    })

    const useCardLayout = Object.keys(cardLayoutPages).includes(pageTitle)
    // todo: replace this logic when page is cutover
    const isBillingsPage = location.pathname.includes('billing')

    const settingsPageContentClassName = useCardLayout
      ? cardLayoutPages[pageTitle]
      : isBillingsPage
      ? 'billing-content-container settings-page-content-container'
      : isViewBrand
      ? 'settings-view-brands-page'
      : 'settings-page-content-container'

    const subnav = this.createSubnav()
    const headerNav = isBrands && HEADER_NAVS.BRANDS

    return (
      <Fragment>
        <Div className="settings-section">
          <Div className="settings-rest">
            {!isDealsPage && subnav}
            {/*TODO: the teammates-page-content-container classname below can be removed once all settings pages have card layout*/}
            {isV2Settings ? (
              <SettingsContent orgId={orgId} hasDesktopApp={hasDesktopApp} />
            ) : (
              <Div className={settingsPageContentClassName}>
                {pageTitle && (
                  <Div className="settings-page-header-container">
                    <SettingsPageHeader
                      title={pageTitle}
                      breadcrumbs={pageBreadcrumbs}
                      headerNav={headerNav}
                    />
                  </Div>
                )}
                <Div className="settings-page-content">
                  <SettingsContent
                    orgId={orgId}
                    hasDesktopApp={hasDesktopApp}
                  />
                </Div>
              </Div>
            )}
          </Div>
        </Div>
        <Div id="settings-web-root-portal" />
      </Fragment>
    )
  }
}

const mapStateToProps = (state) => ({
  appConfig: UserSelectors.appConfig(state),
  orgId: state.user.orgId,
  org: state.org,
  userId: state.user.id,
  user: state.user,
  hasDesktopApp: UserSelectors.hasDesktopApp(state),
  bootstrapComplete: path(['config', 'bootstrapComplete'], state),
})

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        recieveUserProperties,
        receiveHasMultiEmbed,
        receiveHasMultiLanguageDowngrade,
      },
      dispatch
    ),
  }
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Settings))
